import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
  TagIcon,
  TruckIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  ClockIcon,
  MapPinIcon,
  CalendarIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import OrderTimeline from '../components/OrderTimeline';
import SupplierOrderStatusDialog, { OrderTransition } from './SupplierOrderStatusDialog';
import {
  downloadSupplierOrderInvoice,
  fetchSupplierOrderDetail,
  transitionSupplierOrderStatus,
} from '../api/marketplaceApi';
import type { SupplierOrder, SupplierOrderItem } from '../types';

const STATUS_LABELS: Record<string, string> = {
  pending_supplier_confirmation: 'Awaiting Confirmation',
  confirmed: 'Confirmed',
  stock_reserved: 'Stock Reserved',
  preparing: 'Preparing',
  ready_for_quality_check: 'Ready for Quality Check',
  quality_accepted: 'Quality Accepted',
  quality_rejected: 'Quality Rejected',
  correction_required: 'Correction Required',
  ready_for_dispatch: 'Ready for Dispatch',
  in_transit: 'In Transit',
  delivered: 'Delivered',
  buyer_accepted: 'Buyer Accepted',
  completed: 'Completed',
  cancelled: 'Cancelled',
  disputed: 'Disputed',
};

const TIMELINE_EVENTS = [
  { status: 'pending_supplier_confirmation', label: 'Order received', placement: 'start' },
  { status: 'confirmed', label: 'Order confirmed', placement: 'middle' },
  { status: 'stock_reserved', label: 'Stock reserved', placement: 'middle' },
  { status: 'preparing', label: 'Preparation started', placement: 'middle' },
  { status: 'ready_for_quality_check', label: 'Ready for quality check', placement: 'middle' },
  { status: 'quality_accepted', label: 'Quality accepted', placement: 'middle' },
  { status: 'quality_rejected', label: 'Quality rejected', placement: 'middle' },
  { status: 'correction_required', label: 'Correction required', placement: 'middle' },
  { status: 'ready_for_dispatch', label: 'Ready for dispatch', placement: 'middle' },
  { status: 'in_transit', label: 'Dispatched', placement: 'middle' },
  { status: 'delivered', label: 'Delivered', placement: 'end' },
  { status: 'buyer_accepted', label: 'Buyer accepted', placement: 'end' },
  { status: 'completed', label: 'Completed', placement: 'end' },
  { status: 'cancelled', label: 'Cancelled', placement: 'end' },
  { status: 'disputed', label: 'Disputed', placement: 'end' },
];

function statusTimelineEntry(
  orderStatus: string,
  createdAt: string,
  updatedAt: string
): { status: string; label: string; timestamp: string }[] {
  const entries: { status: string; label: string; timestamp: string }[] = [];

  const orderReceived: { status: string; label: string; timestamp: string } = {
    status: 'pending_supplier_confirmation',
    label: `Order ${STATUS_LABELS.pending_supplier_confirmation.toLowerCase()}`,
    timestamp: createdAt,
  };
  entries.push(orderReceived);

  if (orderStatus === 'pending_supplier_confirmation') {
    return entries;
  }

  const confirmed: { status: string; label: string; timestamp: string } = {
    status: 'confirmed',
    label: STATUS_LABELS.confirmed,
    timestamp: updatedAt,
  };
  entries.push(confirmed);

  if (orderStatus === 'confirmed') {
    return entries;
  }

  // Walk through the known timeline up to current status
  const sequence = TIMELINE_EVENTS
    .filter((e) => e.placement === 'middle' || e.placement === 'end')
    .filter((e) => {
      const idx = TIMELINE_EVENTS.findIndex((t) => t.status === e.status);
      const currentIdx = TIMELINE_EVENTS.findIndex((t) => t.status === orderStatus);
      return idx < currentIdx || (idx === currentIdx && e.placement === 'end');
    });

  for (const ev of sequence) {
    const label =
      ev.status === orderStatus
        ? STATUS_LABELS[ev.status]
        : `Moved to ${STATUS_LABELS[ev.status].toLowerCase()}`;
    entries.push({
      status: ev.status,
      label,
      timestamp: ev.status === orderStatus ? updatedAt : updatedAt,
    });
  }

  return entries;
}

const SupplierOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [order, setOrder] = useState<SupplierOrder | null>(null);
  const [items, setItems] = useState<SupplierOrderItem[]>([]);
  const [shipments, setShipments] = useState<any[]>([]);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [reservations, setReservations] = useState<any[]>([]);
  const [settlements, setSettlements] = useState<any[]>([]);
  const [disputes, setDisputes] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [allowedActions, setAllowedActions] = useState<OrderTransition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showTransitionDialog, setShowTransitionDialog] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [justTransitioned, setJustTransitioned] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSupplierOrderDetail(parseInt(id, 10));
      setOrder(res.order);
      setItems((res.items || []) as SupplierOrderItem[]);
      setShipments(res.shipments || []);
      setInvoices(res.invoices || []);
      setReservations(res.reservations || []);
      setSettlements(res.settlements || []);
      setDisputes(res.disputes || []);
      setEvents(res.events || []);
      setAllowedActions(res.allowedTransitions || []);
    } catch (err: any) {
      console.error('Failed to load supplier order:', err);
      setError(err.message || 'Failed to load order details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleTransition = async (selectedTransition: OrderTransition, note: string) => {
    if (!id) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      await transitionSupplierOrderStatus(
        parseInt(id, 10),
        selectedTransition.state,
        note || undefined
      );
      setShowTransitionDialog(false);
      setJustTransitioned(true);
      setTimeout(() => setJustTransitioned(false), 3000);
      await loadOrder();
    } catch (err: any) {
      setTransitionError(err.message || 'Failed to update status');
    } finally {
      setTransitioning(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!order) return;
    setDownloadingInvoice(true);
    try {
      await downloadSupplierOrderInvoice(order.id, order.order_number);
    } catch (err: any) {
      setError(err.message || 'Failed to download invoice');
    } finally {
      setDownloadingInvoice(false);
    }
  };

  const getItemUnitPrice = (item: SupplierOrderItem): number => {
    if (item.unit_price != null) return Number(item.unit_price);
    if (item.total_price != null && item.commercial_quantity > 0) {
      return Number(item.total_price) / Number(item.commercial_quantity);
    }
    return 0;
  };

  const subtotal = items.reduce(
    (s, i) => s + Number(i.total_price || 0),
    0
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
        <ExclamationTriangleIcon
          className="w-10 h-10 text-amber-400 mx-auto mb-3"
        />
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          Order not available
        </h3>
        <p className="text-sm text-gray-500 mb-4">{error || 'This order could not be loaded.'}</p>
        <button
          onClick={() => navigate('/marketplace/supplier/orders')}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Orders
        </button>
      </div>
    );
  }

  const timeline = events.length > 0
    ? events.map(event => ({
        status: event.next_state || event.previous_state || order.status,
        label: String(event.event_type || 'Order updated').replaceAll('_', ' '),
        timestamp: event.created_at,
        actor: event.actor_name || 'System',
        notes: event.reason,
      }))
    : statusTimelineEntry(order.status, order.created_at, order.updated_at);

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate('/marketplace/supplier/orders')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Orders
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-sm font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-md">
              {order.order_number}
            </span>
            <StatusBadge status={order.status} />
            {order.status === 'pending_supplier_confirmation' && (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                Awaiting your confirmation
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Parent order:{' '}
            <span className="font-mono text-gray-700">
              {order.parent_order_number || '—'}
            </span>
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleDownloadInvoice}
            disabled={downloadingInvoice}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
          >
            {downloadingInvoice ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <DocumentArrowDownIcon className="w-4 h-4" />}
            Invoice PDF
          </button>
          {allowedActions.length > 0 && (
            <button
              onClick={() => setShowTransitionDialog(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              Update Status
            </button>
          )}
          <button
            onClick={() => navigate('/marketplace/supplier/orders')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Close"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        {/* Left: Items */}
        <div className="xl:col-span-2 space-y-5">
          {/* Order Items */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <TagIcon className="w-4 h-4 text-emerald-500" />
                Order Items
              </h3>
              <span className="text-xs text-gray-400">{items.length} item{items.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Product
                    </th>
                    <th className="px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                      Quantity
                    </th>
                    <th className="px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                      Unit Price
                    </th>
                    <th className="px-5 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-3">
                        <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                        {item.catalog_name && (
                          <p className="text-xs text-gray-400 mt-0.5">{item.catalog_name}</p>
                        )}
                        {item.grade && (
                          <span className="inline-block mt-1 text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            {item.grade}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-right text-gray-600 text-sm">
                        <span className="font-mono">
                          {Number(item.commercial_quantity).toLocaleString()}
                          {' '}
                          <span className="text-gray-400">{item.commercial_unit}</span>
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right text-gray-600 text-sm">
                        <Money
                          amount={getItemUnitPrice(item)}
                          currency={item.currency}
                        />
                      </td>
                      <td className="px-5 py-3 text-right font-mono font-medium text-gray-900 text-sm">
                        <Money amount={item.total_price} currency={item.currency} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-gray-200 bg-gray-50/50">
                  <tr>
                    <td colSpan={3} className="px-5 py-3 text-right font-semibold text-gray-900 text-sm">
                      Subtotal
                    </td>
                    <td className="px-5 py-3 text-right font-mono font-bold text-gray-900 text-sm">
                      <Money amount={subtotal} currency={order.currency} />
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Commission summary */}
          {order.commission_amount != null &&
           order.commission_amount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <CurrencyDollarIcon className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">
                    Commission Breakdown
                  </p>
                  <div className="mt-2 text-sm text-amber-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Commission ({Number(order.commission_percentage || 0).toFixed(1)}%)</span>
                      <span className="font-mono">
                        -<Money amount={order.commission_amount} currency={order.currency} />
                      </span>
                    </div>
                    <div className="flex justify-between font-semibold text-amber-900 border-t border-amber-200 pt-1 mt-1">
                      <span>You receive</span>
                      <span className="font-mono">
                        <Money amount={order.total_amount} currency={order.currency} />
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Notes */}
          {order.supplier_notes && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-blue-800">Supplier Notes</p>
                  <p className="text-sm text-blue-700 mt-1 whitespace-pre-wrap">
                    {order.supplier_notes}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <OperationalList title="Shipments" icon={<TruckIcon className="w-4 h-4" />} rows={shipments} primary="shipment_number" empty="No shipment has been created." />
            <OperationalList title="Invoices" icon={<DocumentTextIcon className="w-4 h-4" />} rows={invoices} primary="invoice_number" empty="No invoice record yet. The order PDF remains available above." />
            <OperationalList title="Stock reservations" icon={<ArchiveBoxIcon className="w-4 h-4" />} rows={reservations} primary="listing_title" empty="No stock reservation is linked." />
            <OperationalList title="Settlements" icon={<CurrencyDollarIcon className="w-4 h-4" />} rows={settlements} primary="settlement_number" empty="No settlement has been created." />
          </div>

          {disputes.length > 0 && (
            <OperationalList title="Disputes" icon={<ExclamationTriangleIcon className="w-4 h-4" />} rows={disputes} primary="dispute_number" empty="" />
          )}
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          {/* Buyer info */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <UserGroupIcon className="w-4 h-4 text-emerald-500" />
              Buyer Information
            </h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Parent Order</span>
                <span className="font-mono text-gray-700 text-xs">
                  {order.parent_order_number || '—'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Buyer</span>
                <span className="text-gray-900 font-medium">
                  {order.buyer_name || '—'}
                </span>
              </div>
              {order.delivery_address && (
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500 flex items-center gap-1">
                    <MapPinIcon className="w-3.5 h-3.5" />
                    Delivery
                  </span>
                  <span className="text-gray-700 text-xs leading-tight text-right max-w-[60%]">
                    {order.delivery_address}
                  </span>
                </div>
              )}
              {order.delivery_date && (
                <div className="flex justify-between">
                  <span className="text-gray-500 flex items-center gap-1">
                    <CalendarIcon className="w-3.5 h-3.5" />
                    Delivery date
                  </span>
                  <span className="text-gray-700 text-xs">
                    {new Date(order.delivery_date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Order details */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <BuildingStorefrontIcon className="w-4 h-4 text-emerald-500" />
              Order Details
            </h3>
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Order #</span>
                <span className="font-mono text-gray-900 text-xs">{order.order_number}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Currency</span>
                <span className="font-mono text-gray-700">{order.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Items</span>
                <span className="text-gray-700">{items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Received</span>
                <span className="text-gray-700 text-xs">
                  {new Date(order.created_at).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Last update</span>
                <span className="text-gray-700 text-xs">
                  {new Date(order.updated_at).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <ClockIcon className="w-4 h-4 text-emerald-500" />
              Timeline
            </h3>
            <OrderTimeline events={timeline} currentStatus={order.status} />
          </div>

          {/* Just transitioned toast */}
          {justTransitioned && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 flex items-center gap-2">
              <CheckCircleIcon className="w-4 h-4 text-green-500 shrink-0" />
              Status updated successfully.
            </div>
          )}
        </div>
      </div>

      <SupplierOrderStatusDialog
        open={showTransitionDialog}
        transitions={allowedActions}
        loading={transitioning}
        error={transitionError}
        onClose={() => { setShowTransitionDialog(false); setTransitionError(null); }}
        onSubmit={handleTransition}
      />
    </div>
  );
};

const OperationalList: React.FC<{ title: string; icon: React.ReactNode; rows: any[]; primary: string; empty: string }> = ({ title, icon, rows, primary, empty }) => (
  <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
    <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-800">{icon}{title}</h3>
      <span className="text-xs text-slate-500">{rows.length}</span>
    </header>
    {rows.length === 0 ? <p className="p-4 text-sm text-slate-500">{empty}</p> : <ul className="divide-y divide-slate-100">{rows.map(row => (
      <li key={row.id} className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="min-w-0"><p className="truncate text-sm font-semibold text-slate-900">{row[primary] || `#${row.id}`}</p>{row.quantity != null && <p className="text-xs text-slate-500">{Number(row.quantity).toLocaleString()} {row.unit}</p>}</div>
        <StatusBadge status={row.status || 'pending'} size="sm" />
      </li>
    ))}</ul>}
  </section>
);

export default SupplierOrderDetail;
